// Фаза 9.4 (решение владельца 2026-07-03) — мастер «Загрузить исправленную
// редакцию»: пакет файлов → автосопоставление с томами по имени (бэкенд
// /revision-match) → таблица подтверждения (новая версия тома / новый документ /
// пропустить) → последовательная загрузка → создание редакции проекта.
// Автосопоставление предлагает — человек подтверждает.
import { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PackagePlus, X, ArrowRight, CheckCircle2, AlertTriangle, Lock } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { toast } from '@/state/toast';
import type { Project } from '@/types/api';
import { canRedaction, tariffStateFromProject } from '@/lib/tariff';

interface MatchRow {
  filename: string;
  document_id: string | null;
  document_title: string | null;
  confidence: 'exact' | 'fuzzy' | 'none';
  score: number;
}

interface DocOption { document_id: string; title: string; }

// Решение пользователя по каждому файлу пакета.
type Action =
  | { kind: 'version'; documentId: string }   // новая версия существующего тома
  | { kind: 'new' }                            // новый документ
  | { kind: 'skip' };                          // не загружать

type Step = 'idle' | 'plan' | 'uploading' | 'done';

export function RevisionUploadWizard({ projectId, documents }: {
  projectId: string;
  documents: DocOption[];
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  // 15.E: тариф проекта — исправленная редакция доступна только на full.
  const projectQ = useQuery<Project>({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    staleTime: 1000 * 30,
  });
  const redactionAllowed = canRedaction(tariffStateFromProject(projectQ.data));
  const [step, setStep] = useState<Step>('idle');
  const [files, setFiles] = useState<File[]>([]);
  const [rows, setRows] = useState<MatchRow[]>([]);
  const [actions, setActions] = useState<Record<string, Action>>({});
  const [progress, setProgress] = useState<{ done: number; ok: number; errors: string[] }>({ done: 0, ok: 0, errors: [] });
  const [snapshotMade, setSnapshotMade] = useState<boolean | null>(null);

  const pickFiles = async (list: FileList | null) => {
    const fs = Array.from(list || []);
    if (!fs.length) return;
    setFiles(fs);
    try {
      const res = await api.post<{ matches: MatchRow[] }>(
        `/projects/${projectId}/revision-match`,
        { filenames: fs.map((f) => f.name) },
      );
      setRows(res.matches);
      const init: Record<string, Action> = {};
      for (const m of res.matches) {
        init[m.filename] = m.document_id
          ? { kind: 'version', documentId: m.document_id }
          : { kind: 'new' };
      }
      setActions(init);
      setStep('plan');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось сопоставить файлы');
    }
  };

  const upload = async () => {
    setStep('uploading');
    const errors: string[] = [];
    let ok = 0;
    let done = 0;
    for (const f of files) {
      const act = actions[f.name] || { kind: 'new' };
      if (act.kind === 'skip') { done += 1; setProgress({ done, ok, errors: [...errors] }); continue; }
      const fd = new FormData();
      fd.append('file', f);
      fd.append('project_id', projectId);
      if (act.kind === 'version') fd.append('document_id', act.documentId);
      try {
        const res = await fetch('/ingest', { method: 'POST', credentials: 'include', body: fd });
        if (!res.ok) {
          let detail = `HTTP ${res.status}`;
          try {
            const body = await res.json();
            if (body && typeof body.detail === 'string') detail = body.detail;
          } catch {/* ignore */}
          throw new ApiError(res.status, detail);
        }
        ok += 1;
      } catch (e) {
        errors.push(`${f.name}: ${e instanceof Error ? e.message : 'ошибка'}`);
      }
      done += 1;
      setProgress({ done, ok, errors: [...errors] });
    }
    // Редакция проекта: создаём, если хоть один файл загружен.
    let snap = false;
    if (ok > 0) {
      try {
        await api.post(`/projects/${projectId}/snapshots`, {});
        snap = true;
      } catch {
        // «no changes» или гонка — не ошибка мастера; редакцию можно создать кнопкой.
        snap = false;
      }
    }
    setSnapshotMade(snap);
    qc.invalidateQueries({ queryKey: ['documents', projectId] });
    qc.invalidateQueries({ queryKey: ['snapshots', projectId] });
    setStep('done');
  };

  const reset = () => {
    setStep('idle'); setFiles([]); setRows([]); setActions({});
    setProgress({ done: 0, ok: 0, errors: [] }); setSnapshotMade(null);
  };

  const CONF_LABEL: Record<MatchRow['confidence'], { text: string; color: string }> = {
    exact: { text: 'совпадение по имени', color: '#047857' },
    fuzzy: { text: 'похоже на этот том — проверьте', color: '#B45309' },
    none:  { text: 'пара не найдена', color: '#6B7280' },
  };

  if (step === 'idle') {
    // 15.E: на тарифе null/express загрузка исправленной редакции недоступна.
    if (!redactionAllowed) {
      return (
        <div className="flex items-center" style={{ gap: 8, padding: '10px 18px', borderRadius: 22,
             fontSize: 13, fontWeight: 600, border: '1px dashed #E5E7EB', background: '#F9FAFB',
             color: '#6B7280', cursor: 'not-allowed' }}
             title="Доступно на тарифе «Доведение до экспертизы»">
          <Lock size={16} />
          Загрузить исправленную редакцию · доступно на тарифе «Доведение до экспертизы»
        </div>
      );
    }
    return (
      <>
        <input ref={inputRef} type="file" multiple accept=".pdf,.dxf,.xlsx,.xls,.pptx,.html,.htm"
               style={{ display: 'none' }}
               onChange={(e) => { void pickFiles(e.target.files); e.target.value = ''; }} />
        <button type="button" onClick={() => inputRef.current?.click()}
          className="flex items-center"
          style={{ gap: 8, padding: '10px 18px', borderRadius: 22, fontSize: 13, fontWeight: 600,
                   border: '1px solid #0A1628', background: 'transparent', color: '#0A1628', cursor: 'pointer' }}>
          <PackagePlus size={16} />
          Загрузить исправленную редакцию
        </button>
      </>
    );
  }

  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 24, marginTop: 16 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <div className="text-ink" style={{ fontSize: 15, fontWeight: 650 }}>
          Загрузка исправленной редакции · {files.length} файлов
        </div>
        {step !== 'uploading' && (
          <button type="button" onClick={reset} title="Закрыть"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 4 }}>
            <X size={18} />
          </button>
        )}
      </div>

      {step === 'plan' && (
        <>
          <div className="text-muted" style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 14, maxWidth: 720 }}>
            Проверьте сопоставление: для каждого файла выберите, к какому тому он относится
            (станет новой версией файла этого тома), отметьте как новый документ — или пропустите.
            После загрузки будет создана новая редакция проекта.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map((m) => {
              const act = actions[m.filename] || { kind: 'new' };
              const conf = CONF_LABEL[m.confidence];
              return (
                <div key={m.filename} className="border border-border rounded-card"
                     style={{ padding: '10px 14px', display: 'grid', gridTemplateColumns: '1.2fr auto 1.3fr', gap: 12, alignItems: 'center' }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="text-ink" style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.filename}>
                      {m.filename}
                    </div>
                    <div style={{ fontSize: 11, color: conf.color }}>{conf.text}</div>
                  </div>
                  <ArrowRight size={15} stroke="#9CA3AF" />
                  <div className="flex items-center" style={{ gap: 8 }}>
                    <select
                      value={act.kind === 'version' ? act.documentId : act.kind}
                      onChange={(e) => {
                        const v = e.target.value;
                        setActions((p) => ({
                          ...p,
                          [m.filename]: v === 'new' ? { kind: 'new' } : v === 'skip' ? { kind: 'skip' } : { kind: 'version', documentId: v },
                        }));
                      }}
                      style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12.5, minWidth: 0 }}
                    >
                      <option value="new">➕ Новый документ</option>
                      <option value="skip">— Пропустить (не загружать)</option>
                      <optgroup label="Новая версия файла тома:">
                        {documents.map((d) => (
                          <option key={d.document_id} value={d.document_id}>{d.title}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center" style={{ gap: 12, marginTop: 16 }}>
            <button type="button" onClick={() => { void upload(); }}
              style={{ padding: '10px 20px', borderRadius: 22, fontSize: 13, fontWeight: 600,
                       background: '#0A1628', color: '#FFF', border: 'none', cursor: 'pointer' }}>
              Загрузить и создать редакцию
            </button>
            <span className="text-muted" style={{ fontSize: 12 }}>
              новых версий: {Object.values(actions).filter((a) => a.kind === 'version').length} ·
              новых документов: {Object.values(actions).filter((a) => a.kind === 'new').length} ·
              пропущено: {Object.values(actions).filter((a) => a.kind === 'skip').length}
            </span>
          </div>
        </>
      )}

      {step === 'uploading' && (
        <div>
          <div style={{ height: 7, borderRadius: 5, background: '#F3F4F6', overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ height: '100%', width: `${(100 * progress.done) / Math.max(1, files.length)}%`, background: '#0A1628', transition: 'width .4s' }} />
          </div>
          <div className="text-muted" style={{ fontSize: 12.5 }}>
            загружено {progress.done} из {files.length}…
          </div>
        </div>
      )}

      {step === 'done' && (
        <div>
          <div className="flex items-center" style={{ gap: 8, marginBottom: 8 }}>
            {progress.errors.length === 0
              ? <CheckCircle2 size={18} stroke="#10B981" />
              : <AlertTriangle size={18} stroke="#B45309" />}
            <span className="text-ink" style={{ fontSize: 13.5, fontWeight: 600 }}>
              Загружено {progress.ok} из {files.length}
              {snapshotMade ? ' · создана новая редакция проекта' : ''}
            </span>
          </div>
          {snapshotMade === false && progress.ok > 0 && (
            <div className="text-muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Редакция не создана автоматически (возможно, содержимое не изменилось) —
              её можно создать на этой вкладке после завершения анализа.
            </div>
          )}
          {progress.errors.length > 0 && (
            <div style={{ padding: 10, borderRadius: 8, background: 'rgba(245,158,11,.08)', fontSize: 12, color: '#92400E', marginBottom: 8 }}>
              {progress.errors.map((e, i) => <div key={i}>· {e}</div>)}
            </div>
          )}
          <div className="text-muted" style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>
            После завершения анализа документов перезапустите проверки на вкладке
            «Готовность к экспертизе» — дельты посчитаются к предыдущей редакции.
          </div>
          <button type="button" onClick={reset}
            style={{ padding: '8px 16px', borderRadius: 20, fontSize: 12.5, fontWeight: 600,
                     border: '1px solid #E5E7EB', background: 'transparent', color: '#374151', cursor: 'pointer' }}>
            Готово
          </button>
        </div>
      )}
    </div>
  );
}
